// src/pages/reports/TimetabledSubsReport.tsx
//
// Daily Organiser report: sums load_letters.timetabled_subs per teacher
// across their current (non-superseded, released/acknowledged/queried)
// letters for the selected year.

import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import { TableSkeleton } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import { useToast } from '../../contexts/ToastContext';
import { useData } from '../../contexts/DataContext';
import { gridsService } from '../../services/gridsService';
import { getLoadLetterSettings } from '../../services/loadLetterService';
import {
  getTimetabledSubsTotal,
  type TimetabledSubsRow,
} from '../../services/dailyOrganiserReportsService';
import { exportReport, Formatters, type ExportData } from '../../utils/reportExport';
import { displayLearningArea } from '../../constants/learningAreas';

type SemesterFilter = 'semester1' | 'semester2' | 'both';

// Default to the active academic period: Semester 2 from May onwards, else
// Semester 1. Mirrors the load-letter generation page's default.
const currentSemester = (): SemesterFilter =>
  new Date().getMonth() >= 4 ? 'semester2' : 'semester1';

const semesterLabel = (s: SemesterFilter): string =>
  s === 'semester1' ? 'Semester 1' : s === 'semester2' ? 'Semester 2' : 'Both (combined)';

const TimetabledSubsReport: React.FC = () => {
  const { addToast } = useToast();
  const { departments } = useData();

  const [years, setYears] = useState<number[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [department, setDepartment] = useState<string>('all');
  const [semester, setSemester] = useState<SemesterFilter>(currentSemester);
  const [activeOnly, setActiveOnly] = useState(true);
  const [rows, setRows] = useState<TimetabledSubsRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState<keyof TimetabledSubsRow>('timetabledSubs');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    (async () => {
      try {
        const [available, settings] = await Promise.all([
          gridsService.getAvailableYears(),
          getLoadLetterSettings(),
        ]);
        setYears(available);
        const defaultYear = settings?.currentAcademicYear
          ? Number(settings.currentAcademicYear)
          : available[0] ?? new Date().getFullYear();
        setYear(defaultYear);
      } catch (err) {
        console.error('Failed to load year options:', err);
        addToast('Could not load year options', 'error');
      }
    })();
  }, [addToast]);

  useEffect(() => {
    if (year == null) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await getTimetabledSubsTotal({ year, department, activeOnly, semester });
        if (!cancelled) setRows(result);
      } catch (err) {
        console.error('Subs report failed:', err);
        if (!cancelled) addToast('Failed to generate report', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, department, activeOnly, semester, addToast]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];

      // Push null/undefined to the end regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'desc' ? bv - av : av - bv;
      }
      const as = String(av);
      const bs = String(bv);
      return sortDir === 'desc' ? bs.localeCompare(as) : as.localeCompare(bs);
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const totals = useMemo(
    () =>
      sortedRows.reduce(
        (acc, r) => {
          acc.targetMinutes += r.targetMinutes;
          acc.allocatedMinutes += r.allocatedMinutes;
          acc.timetabledSubs += r.timetabledSubs;
          acc.lettersIncluded += r.lettersIncluded;
          return acc;
        },
        { targetMinutes: 0, allocatedMinutes: 0, timetabledSubs: 0, lettersIncluded: 0 }
      ),
    [sortedRows]
  );

  const handleSort = (key: keyof TimetabledSubsRow) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    const data: ExportData = {
      title: 'Timetabled Subs Total',
      description: `Total under-allocation minutes per teacher for ${year} (${semesterLabel(semester)})`,
      metadata: {
        'Generated': new Date().toLocaleDateString(),
        'Year': String(year),
        'Semester': semesterLabel(semester),
        'Learning Area': department === 'all' ? 'All Learning Areas' : department,
        'Teachers': String(sortedRows.length),
      },
      columns: [
        { header: 'Teacher', key: 'staffName' },
        { header: 'Code', key: 'staffCode' },
        { header: 'Learning Area', key: 'department' },
        { header: 'FTE', key: 'fte', format: (v) => Formatters.decimal(v, 1) },
        { header: 'Target Mins', key: 'targetMinutes' },
        { header: 'Allocated Mins', key: 'allocatedMinutes' },
        { header: 'Timetabled Subs', key: 'timetabledSubs' },
        { header: 'Letters Included', key: 'lettersIncluded' },
      ],
      rows: sortedRows,
    };
    exportReport(data, format);
    addToast(`Report exported as ${format.toUpperCase()}`, 'success');
  };

  return (
    <div>
      <PageHeader
        title="Timetabled Subs Total"
        description="Spare (under-allocated) minutes per teacher, from the released load letters — the pool available for timetabled cover"
        actions={
          <>
            <select
              value={year ?? ''}
              onChange={e => setYear(Number(e.target.value))}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              value={semester}
              onChange={e => setSemester(e.target.value as SemesterFilter)}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
            >
              <option value="semester1">Semester 1</option>
              <option value="semester2">Semester 2</option>
              <option value="both">Both (combined)</option>
            </select>
            <select
              value={department}
              onChange={e => setDepartment(e.target.value)}
              className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
            >
              <option value="all">All Learning Areas</option>
              {departments.map(d => (
                <option key={d.id} value={d.name}>{d.name}</option>
              ))}
            </select>
            <label className="inline-flex items-center text-body-sm text-ink-700 gap-2 px-2">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={e => setActiveOnly(e.target.checked)}
              />
              Active staff only
            </label>
            <div className="relative">
              <select
                onChange={e => {
                  if (e.target.value) {
                    handleExport(e.target.value as 'csv' | 'excel' | 'pdf');
                    e.target.value = '';
                  }
                }}
                className="border border-ink-300 rounded-md px-3 py-2 bg-white text-body-sm"
                defaultValue=""
              >
                <option value="" disabled>Export</option>
                <option value="csv">CSV</option>
                <option value="excel">Excel</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
          </>
        }
      />

      <Card>
        <p className="text-meta text-ink-500 mb-4">
          <strong>Timetabled Subs</strong> is a teacher's spare capacity:{' '}
          <strong>Target</strong> minutes (their contracted fortnightly load) minus the{' '}
          <strong>Allocated</strong> minutes actually timetabled to them, floored at zero.
          Those spare minutes are what the Daily Organiser can draw on for planned
          in-house cover before booking a casual. Figures are summed from each teacher's
          current released {semesterLabel(semester)} load letter{semester === 'both' ? 's' : ''};
          teachers with no released letter don't appear.
        </p>

        {loading ? (
          <TableSkeleton rows={6} cols={7} />
        ) : sortedRows.length === 0 ? (
          <EmptyState
            icon={<ClipboardList className="h-6 w-6" />}
            title={`No released load letters for ${year} (${semesterLabel(semester)})`}
            description="Generate and release load letters first, or choose a different year or semester above."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-200">
              <thead className="bg-ink-50">
                <tr>
                  <Th label="Teacher" onClick={() => handleSort('staffName')} />
                  <Th label="Learning Area" onClick={() => handleSort('department')} />
                  <Th label="FTE" onClick={() => handleSort('fte')} />
                  <Th label="Target" onClick={() => handleSort('targetMinutes')} />
                  <Th label="Allocated" onClick={() => handleSort('allocatedMinutes')} />
                  <Th label="Subs" onClick={() => handleSort('timetabledSubs')} />
                  <Th label="Letters" onClick={() => handleSort('lettersIncluded')} />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-200">
                {sortedRows.map(r => (
                  <tr key={r.staffId} className="hover:bg-ink-50">
                    <td className="px-4 py-2 text-body-sm text-ink-900">
                      {r.staffName} <span className="text-ink-400 font-mono tabular-nums">({r.staffCode})</span>
                    </td>
                    <td className="px-4 py-2 text-body-sm text-ink-500">{displayLearningArea(r.department)}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-500 font-mono tabular-nums">{r.fte.toFixed(1)}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-700 font-mono tabular-nums">{r.targetMinutes}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-700 font-mono tabular-nums">{r.allocatedMinutes}</td>
                    <td className="px-4 py-2 text-body-sm font-medium text-navy-700 font-mono tabular-nums">{r.timetabledSubs}</td>
                    <td className="px-4 py-2 text-body-sm text-ink-500 font-mono tabular-nums">{r.lettersIncluded}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-ink-50 border-t-2 border-ink-200">
                <tr>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-900" colSpan={3}>
                    Total ({sortedRows.length} teacher{sortedRows.length === 1 ? '' : 's'})
                  </td>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-900 font-mono tabular-nums">
                    {totals.targetMinutes}
                  </td>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-900 font-mono tabular-nums">
                    {totals.allocatedMinutes}
                  </td>
                  <td className="px-4 py-2 text-body-sm font-semibold text-navy-700 font-mono tabular-nums">
                    {totals.timetabledSubs}
                  </td>
                  <td className="px-4 py-2 text-body-sm font-medium text-ink-500 font-mono tabular-nums">
                    {totals.lettersIncluded}
                  </td>
                </tr>
              </tfoot>
            </table>
            <div className="mt-3 text-meta text-ink-500">
              {sortedRows.length} teacher{sortedRows.length === 1 ? '' : 's'} &middot;{' '}
              <span className="font-mono tabular-nums">{totals.timetabledSubs}</span> spare
              minutes per fortnight in total
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const Th: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <th
    onClick={onClick}
    className="px-4 py-2 text-left text-meta font-medium text-ink-500 uppercase tracking-wider cursor-pointer select-none"
  >
    {label}
  </th>
);

export default TimetabledSubsReport;
